import React from 'react';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';

// Tab → daily-picks bucket mapping. Keeps render logic data-driven instead of
// duplicating four near-identical TabsContent blocks.
const TABS = [
  { key: 'long-bull', label: 'Long Bull', bucket: 'Long-Term Bull', tone: 'green' },
  { key: 'short-bull', label: 'Short Bull', bucket: 'Short-Term Bull', tone: 'blue' },
  { key: 'long-bear', label: 'Long Bear', bucket: 'Long-Term Bear', tone: 'orange' },
  { key: 'short-bear', label: 'Short Bear', bucket: 'Short-Term Bear', tone: 'red' },
];

const TONES = {
  green: { row: 'bg-green-50 hover:bg-green-100', icon: 'bg-green-600', badge: 'bg-green-600' },
  blue: { row: 'bg-blue-50 hover:bg-blue-100', icon: 'bg-blue-600', badge: 'bg-blue-600' },
  orange: { row: 'bg-orange-50 hover:bg-orange-100', icon: 'bg-orange-600', badge: 'bg-orange-600' },
  red: { row: 'bg-red-50 hover:bg-red-100', icon: 'bg-red-600', badge: 'bg-red-600' },
};

const PickRow = ({ pick, tone, onPick }) => {
  const t = TONES[tone];
  return (
    <div
      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${t.row}`}
      onClick={() => onPick(pick.symbol)}
      data-testid={`daily-pick-${pick.symbol}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${t.icon} rounded-full flex items-center justify-center text-white font-bold`}>
          {pick.symbol.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{pick.symbol}</p>
          <p className="text-sm text-gray-600">{pick.signals} signals active</p>
        </div>
      </div>
      <Badge className={`${t.badge} text-white`}>{pick.score}/100</Badge>
    </div>
  );
};

/**
 * Live AI daily picks across four directional buckets.
 * `onPick` receives a symbol when the user clicks a row.
 */
const DailyPicksCard = ({ dailyPicks, onPick }) => (
  <Card data-testid="daily-picks-card">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <TrendingUp className="text-purple-600" />
        AI Picks - Live Data
      </CardTitle>
    </CardHeader>
    <CardContent>
      <Tabs defaultValue="long-bull">
        <TabsList className="grid grid-cols-4 w-full">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="space-y-3 mt-4">
            {(dailyPicks?.[t.bucket] || []).map((pick) => (
              <PickRow key={pick.symbol} pick={pick} tone={t.tone} onPick={onPick} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </CardContent>
  </Card>
);

export default DailyPicksCard;
